import { Pagination } from "antd";
import CardSample from "features/cases/blocks/CommonCard";
import SortToggle from "feeds/SortToggle";
import { useDataQuery } from "hooks/useData";
import { useDataQueryParams } from "hooks/useDataQueryParams";
import { useSearchParam } from "hooks/useSearchParam";
import { useSortParam } from "hooks/useSortParam";
import { useUser } from "hooks/useUser";
import React, { useCallback, useEffect } from "react";
import { useLocation } from "react-router-dom";
import type { PaginatedResponse, Problem } from "types/basic";

import TopBar from "./top-bar";

import "./ProblemList.css";

type OwnProps = {
  openLinksInNewWindow?: boolean;
  isEmbedded?: boolean;
};

const getRussianSolutionsWord = (count: number): string => {
  const absCount = Math.abs(count);
  const lastTwoDigits = absCount % 100;
  const lastDigit = absCount % 10;

  if (lastTwoDigits >= 11 && lastTwoDigits <= 14) return "решений";
  if (lastDigit === 1) return "решение";
  if (lastDigit >= 2 && lastDigit <= 4) return "решения";

  return "решений";
};

const ProblemList: React.FC<OwnProps> = ({ openLinksInNewWindow = false }) => {
  const spheres =
    useDataQuery<{ id: number; slug?: string; title: string }[]>("spheres");

  const [rawPage, setPage] = useSearchParam("page");
  const page = Number(rawPage) || 1;

  const [sphereSlug, setSphereSlug] = useSearchParam("sphere");
  const activeSphere = sphereSlug
    ? (spheres?.find((s) => (s.slug || `sphere-${s.id}`) === sphereSlug)?.id ??
      0)
    : 0;

  const { sortField, sortDirection, ordering, dateDisplayMode, onSortChange } =
    useSortParam();

  const casesResponse = useDataQueryParams<PaginatedResponse<Problem>>(
    "cases",
    {
      page,
      sphere: activeSphere || undefined,
      ordering,
    },
  );
  const problems = casesResponse?.results || [];

  const { isEditor, isContributor, isBannedFromPublishing } = useUser();

  const canAddProblems = isEditor || (isContributor && !isBannedFromPublishing);

  const editorAddUrl = `#/problems/add`;

  const onChooseSphere = useCallback(
    (id: number | null) => {
      const sphere = spheres?.find((s) => s.id === id);
      const slug = sphere?.slug || (id ? `sphere-${id}` : undefined);

      setSphereSlug(slug, ["page"]);
    },
    [spheres, setSphereSlug],
  );

  const onPageChange = useCallback(
    (newPage: number) => setPage(String(newPage)),
    [setPage],
  );

  const location = useLocation();

  // Прокрутка к карточке из хэша (напр. после публикации). Подсветку-мигание
  // карточка делает сама через useHashHighlight - здесь только скролл.
  useEffect(() => {
    const anchorId = location.hash.slice(1);

    if (!anchorId || !casesResponse) return;

    document
      .getElementById(anchorId)
      ?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [location.hash, location.key, casesResponse]);

  if (!casesResponse) return <div>Loading problems...</div>;

  return (
    <div className="main-page">
      <div className="catalog-toolbar">
        <TopBar
          activeSphere={activeSphere}
          onChooseSphere={onChooseSphere}
        />

        <SortToggle
          onSortChange={onSortChange}
          initialField={sortField}
          initialDirection={sortDirection}
        />
      </div>

      <div className="problem-grid">
        {canAddProblems && (
          <div
            onClick={(e) => {
              window.location.href = editorAddUrl;
              e.stopPropagation();
            }}
          >
            <CardSample
              parentData={{
                isProblem: true,
                isSolution: false,
                isAttribute: false,
                fromCatalog: true,
              }}
              mode={"wide"}
              key={"new-problem"}
              addUrl={editorAddUrl}
              sectionTitle={"Создание проблемы"}
              title={"Новая проблема"}
            />
          </div>
        )}

        {problems.map((problem: Problem) => {
          if ((!problem?.isListed || problem?.meta?.isDraft) && !isEditor)
            return null;

          const problemLink = `#/problems/${problem.id}/`;

          const solutionCount = Number(problem.solutionCount) || 0;
          const solutionWord = getRussianSolutionsWord(solutionCount);
          const solutionCountText =
            solutionCount > 0 ? `и ${solutionCount} ${solutionWord}` : "";

          const problemLinkText = `Подробности ${solutionCountText}`;

          return (
            <CardSample
              parentData={{
                isProblem: true,
                sphereId: problem?.sphere,
                isSolution: false,
                isAttribute: false,
                problemId: String(problem.id),
                fromCatalog: true,
              }}
              cardContentId={String(problem.id)}
              sectionTitle={"Проблема"}
              objectColor={"#FFF2CB"}
              highlightColor={"#fcd757"}
              mode={"wide"}
              key={"problem" + problem.id}
              meta={problem?.meta}
              title={problem?.title}
              description={problem?.description}
              url={problemLink}
              // editUrl={`${apiHost}/admin/solutions/problem/${problem.id}/change/`}
              linkButtonText={problemLinkText}
              openLinksInNewWindow={openLinksInNewWindow}
              dateDisplayMode={dateDisplayMode}
            />
          );
        })}
      </div>

      {casesResponse.totalPages > 1 && (
        <div className="problem-list-pagination">
          <Pagination
            current={casesResponse.page}
            pageSize={casesResponse.pageSize}
            total={casesResponse.count}
            onChange={onPageChange}
            showSizeChanger={false}
          />
        </div>
      )}
    </div>
  );
};

export default ProblemList;
